import {Component, OnInit, signal, ViewChild} from '@angular/core';
import {OngletGestionEquipeComponent} from '../onglet-gestion-equipe.component';
import {AuthService} from '../../../shared/services/auth-service.service';
import {GroupeDeTravailDto} from './model/groupe-de-travail.dto';
import {finalize, take} from 'rxjs/operators';
import {GroupeDeTravailService} from './service/groupe-de-travail.service';
import {AlertService, ConfirmationAction, ConfirmationData, ConfirmationModalComponent, ConfirmationModalService, isNullOrUndefined} from 'sigale-angular';
import {of} from 'rxjs';
import {UtilisateurDto} from 'src/app/shared/dto/utilisateur.dto';
import {UtilisateurSigalett} from 'src/app/shared/models/utilisateur-sigalett.model';
import {TranslateService} from '@ngx-translate/core';
import {Mode} from '../../../shared/components/selection-zone/model/mode.enum';
import {ZoneDeTravailAvecSiteDto} from '../../../shared/dto/zone-de-travail-avec-site.dto';

@Component({
	selector: 'app-groupe-de-travail',
	templateUrl: './groupe-de-travail.component.html',
	styleUrls: ['./groupe-de-travail.component.scss']
})
export class GroupeDeTravailComponent extends OngletGestionEquipeComponent implements OnInit {
	readonly NOM_ALERTE_ERREUR_GENERALE = 'tabEquipeGestionGroupeDeTravail';

	@ViewChild('confirmationModalComponent') confirmationModalComponent: ConfirmationModalComponent;
	protected readonly of = of;
	groupesDeTravail = signal<GroupeDeTravailDto[]>([]);
	nomGroupeDeTravail = signal<string>('');
	groupesDeTravailSelectionnes = signal<GroupeDeTravailDto []>([]);
	loadingAjouterGroupeDeTravail = signal<boolean>(false);
	loadingSuppressionGroupeDeTravail = signal<boolean>(false);
	groupeDeTravailAModifier = signal<GroupeDeTravailDto>(null);
	loadingModifierUtilisateur = signal<boolean>(false);
	saisieNouveauNomGroupeDeTravail = signal<boolean>(false);

	nouveauNomGroupeDeTravail: string = '';
	utilisateurAAjouter: UtilisateurDto;
	utilisateursSuggestions: UtilisateurSigalett[];
	utilisateursSelectionnes: UtilisateurDto[] = [];
	loadingAjouterUtilisateur: boolean = false;

	constructor(
		authService: AuthService,
		private groupeDeTravailService: GroupeDeTravailService,
		private alertService: AlertService,
		private confirmationModalService: ConfirmationModalService,
		private translateService: TranslateService
	) {
		super(authService);
	}

	ngOnInit(): void {
		this.chargerDonnees();
	}

	protected chargerDonneesImpl() {
		this.nomGroupeDeTravail.set('');
		this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
		this.groupeDeTravailService.recupererGroupesDeTravailParResponsable(this.loginUtilisateurConnecte)
			.pipe(finalize(() => this.loading = false))
			.subscribe({
				next: groupesDeTravailDto => {
					this.groupesDeTravail.set(groupesDeTravailDto);
					this.nomGroupeDeTravail.set('');

					if (groupesDeTravailDto.length > 0) {
						this.selectionnerGroupe(groupesDeTravailDto[0]);
					}
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Ajoute un utilisateur au groupe de travail sélectionné
	 */
	public ajouterUtilisateur(): void {
		const groupe = this.groupeDeTravailAModifier();
		if (!groupe) {
			return;
		}

		if (!this.utilisateurAAjouter) {
			return;
		}

		this.loadingAjouterUtilisateur = true;

		this.groupeDeTravailService.ajouterUtilisateur(groupe.id, this.utilisateurAAjouter.login)
			.pipe(finalize(() => this.loadingAjouterUtilisateur = false))
			.subscribe({
				next: groupeMisAJour => {
					const groupesMaj = this.groupesDeTravail().map(g =>
						g.id === groupeMisAJour.id ? groupeMisAJour : g
					);
					this.groupesDeTravail.set(groupesMaj);
					this.groupeDeTravailAModifier.set(groupeMisAJour);
					this.utilisateurAAjouter = null;
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Retire les utilisateurs sélectionnés du groupe de travail
	 */
	public retirerUtilisateurs(): void {
		const groupe = this.groupeDeTravailAModifier();

		if (!groupe) {
			this.alertService.errorSansCode(
				this.translateService.instant('GROUPE_DE_TRAVAIL.ERREUR.AUCUN_GROUPE_SELECTIONNE'),
				this.NOM_ALERTE_ERREUR_GENERALE
			);
			return;
		}

		if (this.utilisateursSelectionnes.length <= 0) {
			this.alertService.errorSansCode(
				this.translateService.instant('GROUPE_DE_TRAVAIL.ERREUR.AUCUN_UTILISATEUR_SELECTIONNE'),
				this.NOM_ALERTE_ERREUR_GENERALE
			);
			return;
		}

		const logins: string[] = this.utilisateursSelectionnes.map(utilisateur => utilisateur.login);

		this.groupeDeTravailService.retirerUtilisateur(groupe.id, logins)
			.subscribe({
				next: groupeMisAJour => {
					const groupesMaj = this.groupesDeTravail().map(g =>
						g.id === groupeMisAJour.id ? groupeMisAJour : g
					);
					this.groupesDeTravail.set(groupesMaj);
					this.groupeDeTravailAModifier.set(groupeMisAJour);
					this.utilisateursSelectionnes = [];
				},
				error: error => {
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Ajoute un groupe de travail.
	 */
	public ajouterGroupeDeTravail(): void {
		if (isNullOrUndefined(this.nomGroupeDeTravail()) || this.nomGroupeDeTravail() === '') {
			return;
		}
		this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
		this.loadingAjouterGroupeDeTravail.set(true);
		this.groupeDeTravailService.ajouterGroupeDetravail({
			nom: this.nomGroupeDeTravail(),
			loginResponsable: this.loginUtilisateurConnecte,
			zonesDeTravailAutorisees: []
		}).pipe(finalize(() => this.loadingAjouterGroupeDeTravail.set(false)))
			.subscribe({
				next: groupeDeTravailDto => {
					this.groupesDeTravail.set(groupeDeTravailDto);
					this.nomGroupeDeTravail.set('');
				},
				error: error => {
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Supprime les groupes de travail sélectionnés.
	 */
	public supprimerGroupesDeTravail(): void {
		if (!this.groupesDeTravailSelectionnes() || !this.groupesDeTravailSelectionnes().length) {
			return;
		}
		const noms: string[] = this.groupesDeTravailSelectionnes().map(groupDeTravail => groupDeTravail.nom);

		this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
		this.loadingSuppressionGroupeDeTravail.set(true);

		const groupesASupprimer = this.groupesDeTravailSelectionnes();
		const idsGroupesASupprimer = groupesASupprimer.map(groupe => groupe.id);
		const groupeEnModification = this.groupeDeTravailAModifier();

		this.groupeDeTravailService.supprimerGroupesDeTravailParResponsable(noms, this.loginUtilisateurConnecte)
			.pipe(finalize(() => this.loadingSuppressionGroupeDeTravail.set(false)))
			.subscribe({
				next: groupesDeTravailDto => {
					this.groupesDeTravail.set(groupesDeTravailDto);
					this.groupesDeTravailSelectionnes.set([]);

					if (groupeEnModification && idsGroupesASupprimer.includes(groupeEnModification.id)) {
						this.groupeDeTravailAModifier.set(null);
						this.saisieNouveauNomGroupeDeTravail.set(false);
						this.nouveauNomGroupeDeTravail = '';

						if (groupesDeTravailDto.length > 0) {
							this.selectionnerGroupe(groupesDeTravailDto[0]);
						}
					}
				},
				error: error => {
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Ouvre la popup de suppression des groupes de travail.
	 */
	supprimerGroupesDeTravailConfirmation() {
		const boutonOui = new ConfirmationAction('GLOBAL.OUI');
		const boutonNon = new ConfirmationAction('GLOBAL.NON', 'link');

		boutonOui.onClick.pipe(take(1)).subscribe(() => {
			this.supprimerGroupesDeTravail();
			this.confirmationModalService.fermer();
		});

		boutonNon.onClick.pipe(take(1)).subscribe(() => {
			this.confirmationModalService.fermer();
		});

		const confirmationData = new ConfirmationData(
			'GROUPE_DE_TRAVAIL.POPUP_CONFIRMER_SUPPRIMER_GROUPES_DE_TRAVAIL.TITRE',
			'GROUPE_DE_TRAVAIL.POPUP_CONFIRMER_SUPPRIMER_GROUPES_DE_TRAVAIL.MSG_CONFIRMATION_PLURIEL',
			[boutonOui, boutonNon]
		);
		this.confirmationModalService.ouvrir(confirmationData);
	}

	activerDesactiverSaisiNomGroupeDeTravail() {
		this.saisieNouveauNomGroupeDeTravail.set(!this.saisieNouveauNomGroupeDeTravail());
		if (!this.saisieNouveauNomGroupeDeTravail()) {
			const groupeSelectionne = this.groupeDeTravailAModifier();
			if (groupeSelectionne) {
				this.nouveauNomGroupeDeTravail = groupeSelectionne.nom;
			}
		}
	}

	selectionnerGroupePourModification(groupe: GroupeDeTravailDto) {
		this.selectionnerGroupe(groupe);
	}

	modifierGroupeDeTravail() {
		if (!this.nouveauNomGroupeDeTravail?.trim()) {
			this.alertService.errorSansCode(this.translateService.instant('EQUIPE.ERREUR.NOM_EQUIPE_VIDE'), this.NOM_ALERTE_ERREUR_GENERALE);
			return;
		}

		const groupe = this.groupeDeTravailAModifier();
		if (!groupe) {
			this.alertService.errorSansCode(this.translateService.instant('GROUPE_DE_TRAVAIL.ERREUR.GROUPE_INEXISTANT'), this.NOM_ALERTE_ERREUR_GENERALE);
			return;
		}

		this.loadingModifierUtilisateur.set(true);
		this.groupeDeTravailService.mettreAJourNomGroupeDeTravail({
			groupeDeTravailId: groupe.id,
			nom: this.nouveauNomGroupeDeTravail.trim()
		})
			.pipe(finalize(() => this.loadingModifierUtilisateur.set(false)))
			.subscribe({
				next: groupeMisAJour => {
					const groupesMaj = this.groupesDeTravail().map(g =>
						g.id === groupeMisAJour.id ? groupeMisAJour : g
					);
					this.groupesDeTravail.set(groupesMaj);
					this.saisieNouveauNomGroupeDeTravail.set(false);
					this.groupeDeTravailAModifier.set(groupeMisAJour);
					this.nouveauNomGroupeDeTravail = groupeMisAJour.nom;
				},
				error: err => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(err.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	isButtonModifierNomGroupeDeTravailDisabled(): boolean {
		return !this.nouveauNomGroupeDeTravail || !this.nouveauNomGroupeDeTravail.trim();
	}

	selectionnerGroupe(groupe: GroupeDeTravailDto) {
		this.groupeDeTravailAModifier.set(groupe);
		this.saisieNouveauNomGroupeDeTravail.set(false);
		this.nouveauNomGroupeDeTravail = groupe.nom;
	}

	isGroupeSelectionne(groupe: GroupeDeTravailDto): boolean {
		const groupeSelectionne = this.groupeDeTravailAModifier();
		return groupeSelectionne ? groupeSelectionne.id === groupe.id : false;
	}

	onAjoutRetraitZones(zonesSelectionnees: ZoneDeTravailAvecSiteDto[]): void {
		const zonesDeTravailIds = zonesSelectionnees.map(zone => zone.id);
		this.groupeDeTravailService.modifierZoneDeTravailAutorisees(this.groupeDeTravailAModifier().id, zonesDeTravailIds)
			.subscribe({
				next: () => {
					this.groupeDeTravailAModifier.update((groupeDeTravail) => {
						groupeDeTravail.zonesDeTravailAutorisees = zonesSelectionnees;
						return groupeDeTravail;
					});
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	protected readonly Mode = Mode;
}
